import { AllCardsType } from '../../../data/all-cards'
import { NombreTarotista } from '../../../data/personality'
import { ComboWizardData } from '../shared/combo-wizard-props'
import { buildComboPreamble, ComboPreviousStep } from '../../../utils/combo-context'
import { formatCardLabel } from '../../../utils/card-label'
import {
    CLARIDAD_DIRECCION_NAME,
    CLARIDAD_DIRECCION_PREAMBLE,
    CLARIDAD_DIRECCION_STEPS,
    CLARIDAD_DIRECCION_TOTAL_STEPS,
} from './combo-config'

const cardLabel = (card: AllCardsType) => formatCardLabel(card)

const consultaExtra = (data: ComboWizardData) =>
    data.consulta.length > 0
        ? 'ten en cuenta en la tirada la siguiente consulta que realiza el consultante: ' +
          data.consulta +
          ','
        : ''

const consultaDilemaExtra = (data: ComboWizardData) =>
    data.consulta.length > 0
        ? 'ten en cuenta en la tirada la siguiente consulta o dilema que plantea el consultante: ' +
          data.consulta +
          ','
        : ''

const buildStepPrompt = (step: number, cards: AllCardsType[], data: ComboWizardData): string => {
    if (step === 1) {
        return `Quiero que interpretes una **Tirada de Encrucijada del Alma** con 10 cartas, ayudando al consultante ${
            data.consultante
        } a clarificar un dilema o decisión importante desde la perspectiva del alma, ${consultaDilemaExtra(
            data
        )} según estas cartas: **Energía actual de la situación — Contexto actual (¿cómo está realmente la situación que te genera esta duda?):** ${cardLabel(
            cards[0]
        )}, **La raíz del dilema — Raíz del conflicto (¿qué es lo que realmente está en juego detrás de esta decisión?):** ${cardLabel(
            cards[1]
        )}, **Camino A — Energía y lo que aporta — Opción 1 (si tomás este camino, ¿qué energía trae y qué te aporta a nivel del alma?):** ${cardLabel(
            cards[2]
        )}, **Camino B — Energía y lo que aporta — Opción 2 (si tomás este otro camino, ¿qué energía trae y qué te aporta?):** ${cardLabel(
            cards[3]
        )}, **Qué bloquea o complica el Camino A — Obstáculo Opción 1 (¿qué dificultad o resistencia tiene esta opción?):** ${cardLabel(
            cards[4]
        )}, **Qué bloquea o complica el Camino B — Obstáculo Opción 2 (¿qué dificultad o resistencia tiene esta otra opción?):** ${cardLabel(
            cards[5]
        )}, **Consejo del alma / universo — Guía principal (¿qué te recomienda tu parte más sabia para esta decisión?):** ${cardLabel(
            cards[6]
        )}, **Qué necesitás soltar para decidir con claridad — Liberación necesaria (¿qué creencia, miedo o apego te está dificultando ver con claridad?):** ${cardLabel(
            cards[7]
        )}, **Primer paso recomendado — Acción concreta (¿cuál es el paso más alineado que podés dar ahora?):** ${cardLabel(
            cards[8]
        )} y **Visión probable si seguís el consejo — Cierre y orientación (¿cómo se ve el panorama si seguís la recomendación de tu alma?):** ${cardLabel(
            cards[9]
        )}. Explica detalladamente cada carta y su significado en la posición donde ha salido. Teje una lectura coherente sobre la encrucijada: compara ambos caminos con honestidad, sin forzar una opción si las cartas no lo indican, y conecta el dilema con su raíz, los obstáculos de cada opción y la guía del alma. Detecta miedos, apegos y patrones que nublan la decisión. Proporciona orientación clara, práctica y alineada con el crecimiento del consultante. Mantén un tono profesional, empático, equilibrado y comprensible. Dirígete directamente al consultante, no utilices la tercera persona refiriéndote al consultante, aún cuando las consultas se encuentren en tercera persona, adapta la respuesta para que quede en segunda persona. Incluye además en 2 párrafos después de la lectura, una respuesta directa para el consultante y un consejo final (extra-tarot).`
    }

    if (step === 2) {
        return `Quiero que interpretes una **Tirada de Mensaje de tus Guías** con 10 cartas, ayudando al consultante ${
            data.consultante
        } a recibir el mensaje de sus guías espirituales, ángeles o energías de acompañamiento, ${consultaExtra(
            data
        )} según estas cartas: **Quién te está guiando ahora — Quién conecta (¿qué guía, ángel o energía se está acercando en este momento?):** ${cardLabel(
            cards[0]
        )}, **El mensaje principal — Mensaje central (¿cuál es el mensaje más importante que quieren darte ahora?):** ${cardLabel(
            cards[1]
        )}, **Qué te están pidiendo soltar — Liberación (¿qué creencia, apego, situación o emoción te están pidiendo soltar?):** ${cardLabel(
            cards[2]
        )}, **Qué te están invitando a activar — Activación (¿qué energía, cualidad o acción te están invitando a incorporar?):** ${cardLabel(
            cards[3]
        )}, **Consejo para el momento actual — Consejo práctico (¿qué te recomiendan hacer o cómo transitar este momento?):** ${cardLabel(
            cards[4]
        )}, **Apoyo o herramienta disponible — Apoyo disponible (¿qué ayuda o recurso espiritual tenés disponible ahora?):** ${cardLabel(
            cards[5]
        )}, **Área que necesitan que mires — Área de atención (¿hay algo que estén queriendo que prestes atención? a veces es una advertencia suave):** ${cardLabel(
            cards[6]
        )}, **Cómo podés conectar más con ellos — Práctica recomendada (¿qué podés hacer para fortalecer tu conexión espiritual?):** ${cardLabel(
            cards[7]
        )}, **Energía que viene si seguís su consejo — Resultado probable (¿qué energía o cambio se abre si seguís este mensaje?):** ${cardLabel(
            cards[8]
        )} y **Mensaje de cierre / bendición — Cierre sanador (¿qué quieren que sepas o recuerdes como cierre?):** ${cardLabel(
            cards[9]
        )}. Explica detalladamente cada carta y su significado en la posición donde ha salido. Teje una lectura coherente como un mensaje canalizado de las guías: conecta quién guía, el mensaje central, lo que hay que soltar y activar, y el cierre con bendición. Mantén un tono espiritual, cálido, respetuoso y claro, sin ser dogmático ni imponer creencias. Detecta señales de apoyo, advertencias suaves y oportunidades de conexión. Proporciona consejos prácticos y espirituales accesibles. Mantén un tono profesional, empático y comprensible. Dirígete directamente al consultante, no utilices la tercera persona refiriéndote al consultante, aún cuando las consultas se encuentren en tercera persona, adapta la respuesta para que quede en segunda persona. Incluye además en 2 párrafos después de la lectura, una respuesta directa para el consultante y un consejo final (extra-tarot).`
    }

    return `Quiero que interpretes una **Tirada de Temporada Actual del Alma** con 10 cartas, ayudando al consultante ${
        data.consultante
    } a comprender qué estación o ciclo del alma está transitando en este momento de su vida, ${consultaExtra(
        data
    )} según estas cartas: **Energía de la temporada actual (¿qué estación o energía del alma está transitando ahora?):** ${cardLabel(
        cards[0]
    )}, **Intensidad o duración aproximada (¿qué tan fuerte o cuánto tiempo puede durar esta temporada?):** ${cardLabel(
        cards[1]
    )}, **Propósito principal (¿para qué está esta temporada? ¿Qué quiere trabajar mi alma?):** ${cardLabel(
        cards[2]
    )}, **Lección secundaria / sombra (¿qué otra lección o aspecto sombra está presente?):** ${cardLabel(
        cards[3]
    )}, **Qué necesita ser soltado (¿qué está invitado/a a soltar en este ciclo?):** ${cardLabel(
        cards[4]
    )}, **Qué necesita ser cultivado (¿qué está invitado/a a nutrir o abrirse a recibir?):** ${cardLabel(
        cards[5]
    )}, **Influencias externas (¿qué factores externos están afectando esta temporada?):** ${cardLabel(
        cards[6]
    )}, **Resistencia o miedo (¿qué parte del consultante se está resistiendo o teniendo miedo?):** ${cardLabel(
        cards[7]
    )}, **El regalo/bendición que trae (¿cuál es el regalo oculto o la bendición de esta temporada?):** ${cardLabel(
        cards[8]
    )} y **Consejo del alma + transición (¿cómo transitarla con gracia? ¿qué temporada se asoma después?):** ${cardLabel(
        cards[9]
    )}. Explica detalladamente cada carta y su significado en la posición donde ha salido. Teje una lectura coherente sobre el ciclo actual del alma: conecta la energía de la temporada, su propósito, las lecciones, lo que debe soltarse y cultivarse, y la transición hacia lo que viene. Detecta patrones, resistencias y bendiciones ocultas. Proporciona consejos prácticos y espirituales para transitar esta temporada con conciencia y gracia. Mantén un tono profesional, empático, profundo y comprensible. Dirígete directamente al consultante, no utilices la tercera persona refiriéndote al consultante, aún cuando las consultas se encuentren en tercera persona, adapta la respuesta para que quede en segunda persona. Incluye además en 2 párrafos después de la lectura, una respuesta directa para el consultante y un consejo final (extra-tarot).`
}

export const interpretStep = (
    step: number,
    cards: AllCardsType[],
    data: ComboWizardData,
    user: string,
    personality: NombreTarotista,
    previousSteps: ComboPreviousStep[],
    comboId: string
) => {
    const config = CLARIDAD_DIRECCION_STEPS[step - 1]
    const preamble = buildComboPreamble(
        CLARIDAD_DIRECCION_NAME,
        step,
        CLARIDAD_DIRECCION_TOTAL_STEPS,
        previousSteps,
        CLARIDAD_DIRECCION_PREAMBLE
    )
    const consult = preamble + buildStepPrompt(step, cards, data)

    return {
        consult,
        user,
        personality,
        clientName: data.consultante.name,
        clientPhone: data.consultante.phone || '',
        clientAnswer: data.consulta,
        cards,
        type: config.type,
        comboId,
        comboName: CLARIDAD_DIRECCION_NAME,
        comboStep: step,
        comboTotalSteps: CLARIDAD_DIRECCION_TOTAL_STEPS,
    }
}
